// frontend/src/pages/dashboard/accounts/ThreadsModals.js
import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * ThreadsModals handles:
 *   1) Connect Threads Modal (waiting for user to confirm "Connect")
 *   2) Edit/Delete Threads Modal
 *   3) Confirm Deletion Modal
 *
 * Props from Accounts.js:
 *   showThreadsModal, setShowThreadsModal,
 *   threadsAccounts, selectedThreadsIds,
 *   handleThreadsCheckboxChange,
 *   handleConnectSelectedThreads,  // triggered on "Connect" button
 *
 *   showThreadsDeleteModal, setShowThreadsDeleteModal,
 *   showThreadsDeleteConfirmModal, setShowThreadsDeleteConfirmModal,
 *   handleDeleteThreadsAccounts, handleDeleteThreadsConfirm,
 *
 *   accounts (optional, if you want to reference currently connected accounts)
 */
export default function ThreadsModals({
  // "Connect" modal
  showThreadsModal,
  setShowThreadsModal,
  threadsAccounts,
  selectedThreadsIds,
  handleThreadsCheckboxChange,
  handleConnectSelectedThreads,

  // "Delete" modal
  showThreadsDeleteModal,
  setShowThreadsDeleteModal,
  showThreadsDeleteConfirmModal,
  setShowThreadsDeleteConfirmModal,
  handleDeleteThreadsAccounts,
  handleDeleteThreadsConfirm,

  // Entire "accounts" array from Accounts.js if needed for referencing
  accounts,
}) {
  // Find the existing "Threads" object from `accounts`, so we can display connected pages
  const thAcc = accounts
    ? accounts.find((acc) => acc.platform.toLowerCase() === 'threads')
    : null;

  return (
    <>
      {/* 1) CONNECT THREADS MODAL */}
      <Modal show={showThreadsModal} onHide={() => setShowThreadsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Threads Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(!threadsAccounts || threadsAccounts.length === 0) ? (
            <p>No Threads accounts found.</p>
          ) : (
            <ul className="list-group">
              {threadsAccounts.map((acc) => {
                // Use the property name returned by the server
                const userId = acc.threadsId; 
                const checked = selectedThreadsIds.includes(userId);

                return (
                  <li key={userId} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleThreadsCheckboxChange(userId)}
                    />
                    {/* Profile pic if provided */}
                    {acc.threadsProfilePic && (
                      <img
                        src={acc.threadsProfilePic}
                        alt={acc.username || 'ThreadsUser'}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      />
                    )}
                    {/* Username or display name */}
                    <span>{acc.username || 'ThreadsUser'}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowThreadsModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedThreads}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 2) EDIT/DELETE THREADS MODAL */}
      <Modal
        show={showThreadsDeleteModal}
        onHide={() => setShowThreadsDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Threads Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If we have no connected threads or none to delete, show placeholder */}
          {(() => {
            if (!thAcc || !thAcc.connectedPages || thAcc.connectedPages.length === 0) {
              return <p>No connected Threads accounts to delete.</p>;
            }
            return (
              <ul className="list-group">
                {thAcc.connectedPages.map((item) => {
                  // For connected accounts, we might store item.thId in the DB
                  const userId = item.thId;
                  const checked = selectedThreadsIds.includes(userId);

                  return (
                    <li key={userId} className="list-group-item d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={() => handleThreadsCheckboxChange(userId)}
                      />
                      {item.thProfilePic && (
                        <img
                          src={item.thProfilePic}
                          alt={item.username}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        />
                      )}
                      <span>{item.username}</span>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowThreadsDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteThreadsAccounts}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 3) CONFIRM DELETION MODAL */}
      <Modal
        show={showThreadsDeleteConfirmModal}
        onHide={() => setShowThreadsDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these Threads accounts?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowThreadsDeleteConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleDeleteThreadsConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
